import { Tooltip, TableGridColumn } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useMemo } from "react";
import { DomainTableRowData } from "../types";
import { DomainActionsCell } from "../components/DomainActionsCell";
import { DomainAuthCell } from "../components/DomainAuthCell";
import { ReactComponent as InfoIcon } from "../../../assets/info.svg";
import { DomainForwardingCell } from "../components/DomainForwardingCell";

export const useDomainTableColumns = ({
  onForwardingSetupClick,
  onDomainAuthSetupClick,
}: {
  onForwardingSetupClick: () => void;
  onDomainAuthSetupClick: () => void;
}) => {
  const { t } = useTranslations();

  return useMemo<TableGridColumn<DomainTableRowData>[]>(
    () => [
      {
        id: "email-address",
        title: t({
          id: "client-support-page.email-address",
          defaultMessage: "Email Address",
        }),
        grow: true,
        className: "font-secondary font-medium text-[#1A2023]",
        content: ({ displayEmail }) => displayEmail,
      },
      {
        id: "email-forwarding",
        title: t({
          id: "client-support-page.email-forwarding",
          defaultMessage: "Email Forwarding",
        }),
        grow: true,
        content: ({ forwardingStatus }) => (
          <DomainForwardingCell
            forwardingStatus={forwardingStatus}
            onSetupClick={onForwardingSetupClick}
          />
        ),
      },
      {
        id: "domain-auth",
        title: (
          <div className="flex items-center gap-2.5">
            <span>
              {t({
                id: "client-support-page.domain-auth-optional",
                defaultMessage: "Domain Auth (optional)",
              })}
            </span>
            <Tooltip
              placement="top"
              renderTrigger={({ props, ref }) => (
                <InfoIcon ref={ref} {...props} />
              )}
            >
              {t({
                id: "client-support-page.domain-auth-info",
                defaultMessage:
                  "If you want replies to be sent from your own email address, instead of Jugl Inbox email, authenticate your domain with DNS",
              })}
            </Tooltip>
          </div>
        ),
        grow: true,
        content: ({ domainAuthStatus }) => (
          <DomainAuthCell
            domainAuthStatus={domainAuthStatus}
            onSetupClick={onDomainAuthSetupClick}
          />
        ),
      },
      {
        id: "actions",
        title: t({
          id: "client-support-page.actions",
          defaultMessage: "Actions",
        }),
        width: 80,
        className: "flex justify-center",
        content: () => <DomainActionsCell />,
      },
    ],
    [onDomainAuthSetupClick, onForwardingSetupClick, t]
  );
};
